import { useEffect, useState, useRef, useCallback } from 'react'
import { withRouter } from '../utils'
import Map from '../components/Map'
import { DailyReports } from './DailyReports'
import '../App.css'
import { trackPageView } from '../tracker'
import axios from 'axios'

const GCC = ["Saudi Arabia","UAE","Kuwait","Bahrain","Qatar","Oman"]
const ALL_COUNTRIES = [...GCC,"Syria","Lebanon","Palestine","Jordan","Iraq","Yemen","Egypt","Turkey","Iran","Afghanistan","Pakistan","Libya","Tunisia","Algeria","Morocco","Sudan","Somalia","Israel","Ukraine","Russia"]
const COUNTRY_BOUNDS = {"Saudi Arabia":[24.7,45.1],"UAE":[24.5,54.5],"Kuwait":[29.3,47.9],"Bahrain":[26.1,50.5],"Qatar":[25.3,51.2],"Oman":[21.5,57.0],"Syria":[35.0,38.5],"Lebanon":[33.9,35.8],"Palestine":[31.9,35.2],"Jordan":[31.2,36.8],"Iraq":[33.2,44.4],"Yemen":[15.5,48.5],"Egypt":[26.8,30.8],"Turkey":[39.9,32.9],"Iran":[32.4,53.7],"Afghanistan":[33.9,67.7],"Pakistan":[30.4,69.3],"Libya":[26.3,17.2],"Tunisia":[33.9,9.5],"Algeria":[28.0,1.7],"Morocco":[31.8,-7.1],"Sudan":[12.9,30.2],"Somalia":[5.2,46.2],"Israel":[31.0,34.8],"Ukraine":[48.4,31.2],"Russia":[61.5,105.3]}

function App({ navigate }) {
  const [points, setPoints] = useState(null)
  const [events, setEvents] = useState([])
  const [viewMode, setViewMode] = useState('intel')
  const [countries, setCountries] = useState(GCC)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchResults, setSearchResults] = useState([])
  const [userOpen, setUserOpen] = useState(false)
  const [userName, setUserName] = useState("")
  
  const [daysLeft, setDaysLeft] = useState(null)
  const [introStep, setIntroStep] = useState(0) // 0=hidden, 1=intel, 2=map
  const [showAlerts, setShowAlerts] = useState(false)
  const [alertContacts, setAlertContacts] = useState([])
  const [dateFrom, setDateFrom] = useState(() => new Date(Date.now()-30*86400000).toISOString().split('T')[0])
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0])
  const searchTimer = useRef(null)
  const apiHost = process.env.REACT_APP_APIHOST || 'http://localhost:3033'

  useEffect(() => {
    let token = localStorage.getItem('token')
    if (!token) { navigate('/'); return }
    trackPageView('/cockpit')
    // Load saved country preferences
    try {
      const token2 = localStorage.getItem('token');
      if (token2) {
        fetch(apiHost + '/users/me/preferences', { headers: { 'Authorization': 'Bearer ' + token2 } })
          .then(r => r.json())
          .then(d => { if (d.status && d.data?.selectedCountries?.length > 0) setCountries(d.data.selectedCountries) })
          .catch(() => {})
      }
    } catch(e) {}
    // Fetch user name and expiry
    try { const p = JSON.parse(atob((localStorage.getItem("token")||"..").split(".")[1])); fetch(apiHost + "/users/" + p.id).then(r=>r.json()).then(d => { if(d.status) { setUserName(d.data.firstName + " " + d.data.lastName); if(d.data.accessExpiresAt) { const dl = Math.max(0, Math.ceil((new Date(d.data.accessExpiresAt) - new Date()) / (24*60*60*1000))); setDaysLeft(dl) } } }) } catch(e) {}
    // Show intro popup on first visit
    if (!localStorage.getItem('forte_intro_done')) setIntroStep(1)
    const fromDate = new Date(dateFrom).toISOString()
    const toDate = new Date(dateTo); toDate.setHours(23,59,59,999); fetch(`${apiHost}/heatmap/data?from=${fromDate}&to=${toDate.toISOString()}`, {
      headers: { 'Content-Type': 'application/json', Accept: 'application/json, application/geo+json, application/gpx+xml, img/png; charset=utf-8' },
    }).then(r => r.json()).then(setPoints)
  }, [apiHost, navigate, dateFrom])

  useEffect(() => {
    const h = (e) => { if (e.detail?.country) { const c = ALL_COUNTRIES.find(k => k.toLowerCase() === e.detail.country.toLowerCase()) || e.detail.country; setCountries(p => p.includes(c) ? p : [...p, c]) } }
    const msgHandler = (e) => { if (e.data === 'close-alerts') setShowAlerts(false) }
    window.addEventListener('forte-add-country', h)
    window.addEventListener('message', msgHandler)
    return () => { window.removeEventListener('forte-add-country', h); window.removeEventListener('message', msgHandler) }
  }, [])

  const fetchEvents = async (tl, br) => { const r = await axios.post(`${apiHost}/heatmap/events`, { topLeft: tl, bottomRight: br }); setEvents(r.data) }

  const zoomToCountries = useCallback(() => {
    const coords = countries.map(c => COUNTRY_BOUNDS[c]).filter(Boolean)
    if (!coords.length) return
    const lats = coords.map(c => c[0]), lngs = coords.map(c => c[1])
    window.dispatchEvent(new CustomEvent('forte-zoom', { detail: { bounds: [[Math.min(...lats)-3,Math.min(...lngs)-3],[Math.max(...lats)+3,Math.max(...lngs)+3]] } }))
  }, [countries])

  useEffect(() => { const t = setTimeout(zoomToCountries, 500); return () => clearTimeout(t) }, [countries, zoomToCountries])

  const doSearch = (val) => {
    if (val.length < 3) { setSearchResults([]); return }
    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(async () => {
      try { const r = await fetch('https://nominatim.openstreetmap.org/search?format=json&limit=5&q='+encodeURIComponent(val)); setSearchResults(await r.json()) } catch(e) {}
    }, 400)
  }

  const pickResult = (d) => {
    const c = d.display_name.split(',').pop().trim()
    const match = ALL_COUNTRIES.find(k => k.toLowerCase() === c.toLowerCase()) || c
    setCountries(p => { const n = p.includes(match) ? p : [...p, match]; (() => { try { const t2 = localStorage.getItem('token'); if(t2) fetch(apiHost + '/users/me/preferences', { method:'PUT', headers:{'Content-Type':'application/json','Authorization':'Bearer '+t2}, body:JSON.stringify({selectedCountries:n}) }).catch(()=>{}) } catch(e2){} })(); return n })
    if (d.boundingbox) window.dispatchEvent(new CustomEvent('forte-zoom', { detail: { bounds: [[+d.boundingbox[0],+d.boundingbox[2]],[+d.boundingbox[1],+d.boundingbox[3]]] } }))
    setSearchOpen(false); setSearchResults([])
  }

  const chip = { display:'inline-flex',alignItems:'center',gap:'4px',padding:'3px 10px',fontSize:'11px',color:'#C29717',border:'1px solid rgba(194,151,23,0.3)',borderRadius:'10px',cursor:'pointer',whiteSpace:'nowrap',fontFamily:'Inter,sans-serif' }
  const available = ALL_COUNTRIES.filter(c => !countries.includes(c))
  const visible = countries.slice(0, 5)
  const extra = countries.length - 5

  return (
    <div className='app'>
      <style>{`
        .map-toolbar { display: none !important; }
        .map-div { padding-top: 42px !important; height: 100vh !important; }
        .map-container { height: calc(100vh - 47px) !important; }
        .events-container { top: 12px !important; }
        .map-panel-overlay { top: 42px !important; height: calc(100vh - 42px) !important; }
        .map-panel { height: calc(100vh - 42px) !important; }
      `}</style>

      {/* === HEADER === */}
      <div style={{ position:'fixed',top:0,left:0,right:0,zIndex:10000,height:'42px',background:'#1a1a1a',borderBottom:'1px solid rgba(255,255,255,0.06)',display:'flex',alignItems:'center',padding:'0 16px',gap:'12px',fontFamily:'Inter,sans-serif' }}>

        <img src='icons/logo.webp' alt='' style={{ height:16,opacity:0.85,flexShrink:0 }} />

        {/* View toggle */}
        <div style={{ display:'flex',flexShrink:0 }}>
          {['intel','map'].map((v,i) => (
            <button key={v} onClick={() => setViewMode(v)} style={{
              padding:'4px 12px',fontSize:9,textTransform:'uppercase',letterSpacing:'1px',cursor:'pointer',
              fontWeight:viewMode===v?600:400,
              background:viewMode===v?'rgba(194,151,23,0.15)':'transparent',
              color:viewMode===v?'#C29717':'#666',
              border:viewMode===v?'1px solid rgba(194,151,23,0.3)':'1px solid rgba(255,255,255,0.06)',
              borderRadius:i===0?'3px 0 0 3px':'0 3px 3px 0',
            }}>{v==='map'?'Map':'Intelligence'}</button>
          ))}
        </div>

        <div style={{ width:1,height:20,background:'rgba(255,255,255,0.06)',flexShrink:0 }} />

        {/* Search — inline in header */}
        <div style={{ position:'relative',flexShrink:0 }}>
          {searchOpen ? (
            <div>
              <input autoFocus placeholder='Search location...' onChange={e => doSearch(e.target.value)}
                onBlur={() => setTimeout(() => { setSearchOpen(false); setSearchResults([]) }, 200)}
                style={{ width:200,background:'transparent',border:'1px solid rgba(255,255,255,0.1)',borderRadius:3,padding:'3px 8px',fontSize:11,color:'#fff',outline:'none' }} />
              {searchResults.length > 0 && (
                <div style={{ position:'absolute',top:30,left:0,width:280,background:'#1e1e1e',border:'1px solid rgba(255,255,255,0.08)',borderRadius:3,zIndex:99999,maxHeight:200,overflowY:'auto',boxShadow:'0 8px 20px rgba(0,0,0,0.5)' }}>
                  {searchResults.map((d,i) => (
                    <div key={i} onMouseDown={() => pickResult(d)} style={{ padding:'6px 10px',fontSize:11,color:'#ccc',cursor:'pointer',borderBottom:'1px solid rgba(255,255,255,0.03)' }}
                      onMouseEnter={e => e.currentTarget.style.background='rgba(194,151,23,0.08)'}
                      onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                      {d.display_name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <svg onClick={() => setSearchOpen(true)} style={{ cursor:'pointer',opacity:0.5 }} width={16} height={16} viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='1.5'>
              <circle cx='10' cy='10' r='7'/><line x1='15' y1='15' x2='21' y2='21'/>
            </svg>
          )}
        </div>

        <div style={{ width:1,height:20,background:'rgba(255,255,255,0.06)',flexShrink:0 }} />

        {/* Countries */}
        {visible.map(c => (
          <span key={c} style={chip} onClick={() => { setCountries(p => { const n = p.filter(x => x!==c); (() => { try { const t2 = localStorage.getItem('token'); if(t2) fetch(apiHost + '/users/me/preferences', { method:'PUT', headers:{'Content-Type':'application/json','Authorization':'Bearer '+t2}, body:JSON.stringify({selectedCountries:n}) }).catch(()=>{}) } catch(e2){} })(); return n }) }}>
            {c}<span style={{ opacity:0.4,fontSize:8,marginLeft:2 }}>&times;</span>
          </span>
        ))}
        <div style={{ position:'relative',flexShrink:0 }}>
          <span style={{ ...chip,borderStyle:'dashed',background:extra>0?'rgba(194,151,23,0.08)':'transparent' }}
            onClick={() => setPickerOpen(p => !p)}>
            {extra > 0 ? `+${extra}` : '+'}
          </span>
          {pickerOpen && (
            <div style={{ position:'absolute',top:30,left:-100,width:260,maxHeight:320,overflowY:'auto',background:'#1e1e1e',border:'1px solid rgba(255,255,255,0.1)',borderRadius:3,padding:10,zIndex:99999,boxShadow:'0 8px 20px rgba(0,0,0,0.5)' }}>
              <div style={{ fontSize:9,color:'#C29717',textTransform:'uppercase',letterSpacing:1,marginBottom:6 }}>Selected ({countries.length})</div>
              <div style={{ display:'flex',flexWrap:'wrap',gap:4,marginBottom:10 }}>
                {countries.map(c => (
                  <span key={c} style={{ ...chip,fontSize:11 }} onClick={() => setCountries(p => p.filter(x => x!==c))}>
                    {c} <span style={{ opacity:0.4 }}>&times;</span>
                  </span>
                ))}
              </div>
              <div style={{ fontSize:9,color:'#555',textTransform:'uppercase',letterSpacing:1,marginBottom:4,borderTop:'1px solid rgba(255,255,255,0.04)',paddingTop:6 }}>Add</div>
              {available.map(c => (
                <div key={c} onClick={() => { setCountries(p => [...p,c]) }} style={{ padding:'4px 8px',fontSize:12,color:'#888',cursor:'pointer',borderRadius:2 }}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(194,151,23,0.08)'}
                  onMouseLeave={e => e.currentTarget.style.background='transparent'}>{c}</div>
              ))}
            </div>
          )}
        </div>

        <div style={{ width:1,height:20,background:'rgba(255,255,255,0.06)',flexShrink:0 }} />

        {/* Dates */}
        {[{v:dateFrom,s:setDateFrom},{v:dateTo,s:setDateTo}].map((d,i) => (
          <span key={i} style={{ display:'flex',alignItems:'center',gap:i===0?0:0 }}>
            {i===1 && <span style={{ fontSize:11,color:'#444',margin:'0 6px' }}>—</span>}
            <input type='date' value={d.v} onChange={e => d.s(e.target.value)} style={{
              background:'#1a1a1a',border:'1px solid rgba(255,255,255,0.12)',borderRadius:3,
              padding:'4px 8px',fontSize:11,color:'#ccc',fontFamily:'Inter',outline:'none',width:115,colorScheme:'dark',
            }} />
          </span>
        ))}

        {/* Days left */}
        {daysLeft !== null && (
          <span style={{ fontSize:10, color: daysLeft <= 7 ? "#e55" : daysLeft <= 14 ? "#e88a2a" : "#4CAF50", background: daysLeft <= 7 ? "rgba(229,85,85,0.08)" : daysLeft <= 14 ? "rgba(232,138,42,0.08)" : "rgba(76,175,80,0.08)", border: "1px solid " + (daysLeft <= 7 ? "rgba(229,85,85,0.2)" : daysLeft <= 14 ? "rgba(232,138,42,0.2)" : "rgba(76,175,80,0.2)"), borderRadius:3, padding:"3px 10px", fontWeight:500, whiteSpace:"nowrap" }}>{daysLeft} day{daysLeft !== 1 ? "s" : ""} left</span>
        )}

        {/* Spacer */}
        <div style={{ flex:1 }} />

        {/* Alert bell — opens employee alerting panel in both views */}
        <svg onClick={() => setShowAlerts(true)} style={{ cursor:'pointer',opacity:0.5,flexShrink:0 }} width={18} height={18} viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='1.5'>
          <path d='M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9'/><path d='M13.73 21a2 2 0 01-3.46 0'/>
        </svg>

        {/* User */}
        <div style={{ position:'relative',flexShrink:0 }}>
          <svg onClick={() => setUserOpen(p => !p)} style={{ cursor:'pointer',opacity:0.5 }} width={18} height={18} viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='1.5'>
            <path d='M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2'/><circle cx='12' cy='7' r='4'/>
          </svg>
          {userOpen && (
            <div style={{ position:'absolute',top:30,right:0,width:200,background:'#1a1a1a',border:'1px solid rgba(255,255,255,0.08)',borderRadius:3,padding:'6px 0',zIndex:99999,boxShadow:'0 8px 20px rgba(0,0,0,0.4)' }}>
              <div style={{ padding:'8px 12px',borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                <div style={{ fontSize:12,fontWeight:500,color:'#e6e6e6' }}>{userName}</div>
                <div style={{ fontSize:10,color:'#888' }}>{localStorage.getItem('user_role')||'user'}</div>
              </div>
              <div onClick={() => navigate('/account')}
                style={{ padding:'6px 12px',fontSize:11,color:'#ccc',cursor:'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.04)'}
                onMouseLeave={e => e.currentTarget.style.background='transparent'}>My account</div>
              <div onClick={() => navigate('/invite-team')}
                style={{ padding:'6px 12px',fontSize:11,color:'#C29717',cursor:'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background='rgba(194,151,23,0.04)'}
                onMouseLeave={e => e.currentTarget.style.background='transparent'}>Invite team members</div>
              <div onClick={() => { localStorage.removeItem('token'); localStorage.removeItem('user_role'); navigate('/login') }}
                style={{ padding:'6px 12px',fontSize:11,color:'#e55',cursor:'pointer',borderTop:'1px solid rgba(255,255,255,0.04)' }}
                onMouseEnter={e => e.currentTarget.style.background='rgba(220,50,50,0.08)'}
                onMouseLeave={e => e.currentTarget.style.background='transparent'}>Disconnect</div>
            </div>
          )}
        </div>
      </div>

      {/* Employee alerting panel — iframe of the alert UI */}
      {showAlerts && (
        <div onClick={() => setShowAlerts(false)} style={{ position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(8,8,8,0.8)',zIndex:50000,display:'flex',alignItems:'center',justifyContent:'center',backdropFilter:'blur(4px)' }}>
          <div onClick={e => e.stopPropagation()} style={{ width:1020,maxWidth:'98vw',maxHeight:'92vh',background:'#1B1B1B',border:'1px solid rgba(255,255,255,0.06)',borderRadius:3,overflow:'hidden',position:'relative' }}>
            <iframe src='/employee-alert.html' style={{ width:'100%',height:'88vh',border:'none' }} title='Employee Alerting' />
          </div>
        </div>
      )}

      {/* Intro popup */}
      {introStep > 0 && (
        <div style={{ position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.7)',zIndex:50000,display:'flex',alignItems:'center',justifyContent:'center',backdropFilter:'blur(4px)' }}>
          <div style={{ width:560,background:'#1e1e1e',border:'1px solid rgba(194,151,23,0.2)',borderRadius:6,overflow:'hidden',boxShadow:'0 20px 60px rgba(0,0,0,0.5)' }}>
            {/* Header */}
            <div style={{ padding:'16px 24px',borderBottom:'1px solid rgba(255,255,255,0.06)',display:'flex',justifyContent:'space-between',alignItems:'center' }}>
              <div style={{ display:'flex',alignItems:'center',gap:10 }}>
                <img src='icons/logo.webp' alt='' style={{ height:14,opacity:0.8 }} />
                <span style={{ fontSize:10,color:'#C29717',textTransform:'uppercase',letterSpacing:2 }}>Welcome to the cockpit</span>
              </div>
              <div style={{ fontSize:10,color:'#555' }}>{introStep} / 2</div>
            </div>

            {introStep === 1 && (
              <div style={{ padding:'24px' }}>
                <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:12 }}>
                  <div style={{ padding:'4px 10px',fontSize:9,textTransform:'uppercase',letterSpacing:1,color:'#C29717',border:'1px solid rgba(194,151,23,0.3)',borderRadius:3,background:'rgba(194,151,23,0.15)',fontWeight:600 }}>Intelligence</div>
                  <span style={{ fontSize:12,color:'#888' }}>Your default view</span>
                </div>
                <div style={{ fontSize:14,color:'#fff',fontWeight:500,marginBottom:10,lineHeight:1.4 }}>Real-time intelligence feed with daily assessments, forecasts, and analytics.</div>
                <div style={{ fontSize:12,color:'#888',lineHeight:1.7,marginBottom:16 }}>
                  Monitor conflict events across your selected countries with verified intelligence from our GSOC analysts. Access threat scores, forecast reports, arsenal tracking, and airspace monitoring — all from one unified dashboard.
                </div>
                <div style={{ borderRadius:4,overflow:'hidden',border:'1px solid rgba(255,255,255,0.06)',marginBottom:16 }}>
                  <img src='/deck/img/intel_assessment.png' alt='' style={{ width:'100%',display:'block' }} onError={e => e.target.style.display='none'} />
                </div>
                <div style={{ fontSize:11,color:'#666',lineHeight:1.5 }}>
                  Use the tabs (Events, Reports, Forecast, Analytics, Arsenal, Airspace) to navigate between intelligence modules. Countries and dates in the header control what you see.
                </div>
              </div>
            )}

            {introStep === 2 && (
              <div style={{ padding:'24px' }}>
                <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:12 }}>
                  <div style={{ padding:'4px 10px',fontSize:9,textTransform:'uppercase',letterSpacing:1,color:'#888',border:'1px solid rgba(255,255,255,0.1)',borderRadius:3,fontWeight:600 }}>Map</div>
                  <span style={{ fontSize:12,color:'#888' }}>Interactive conflict map</span>
                </div>
                <div style={{ fontSize:14,color:'#fff',fontWeight:500,marginBottom:10,lineHeight:1.4 }}>Heatmap visualization with real-time event plotting across 26+ countries.</div>
                <div style={{ fontSize:12,color:'#888',lineHeight:1.7,marginBottom:16 }}>
                  Every verified incident is geo-pinned on an interactive heatmap. Toggle between marker and heatmap views to identify concentration zones. The Major Events panel on the right shows events within the current map viewport.
                </div>
                <div style={{ borderRadius:4,overflow:'hidden',border:'1px solid rgba(255,255,255,0.06)',marginBottom:16 }}>
                  <img src='/deck/img/heatmap_full.png' alt='' style={{ width:'100%',display:'block' }} onError={e => e.target.style.display='none'} />
                </div>
                <div style={{ fontSize:11,color:'#666',lineHeight:1.5 }}>
                  Switch between views using the <span style={{ color:'#C29717',fontWeight:500 }}>Intelligence</span> / <span style={{ color:'#888',fontWeight:500 }}>Map</span> toggle in the top-left of the header bar.
                </div>
              </div>
            )}

            {/* Footer */}
            <div style={{ padding:'12px 24px',borderTop:'1px solid rgba(255,255,255,0.06)',display:'flex',justifyContent:'space-between',alignItems:'center' }}>
              <div style={{ display:'flex',gap:6 }}>
                {[1,2].map(s => <div key={s} style={{ width:s===introStep?16:6,height:6,borderRadius:3,background:s===introStep?'#C29717':'rgba(255,255,255,0.1)',transition:'all 0.3s' }} />)}
              </div>
              <button onClick={() => {
                if (introStep === 1) { setIntroStep(2) }
                else { setIntroStep(0); localStorage.setItem('forte_intro_done','1'); axios.post(`${apiHost}/heatmap/intro-done`).catch(()=>{}) }
              }} style={{
                padding:'8px 24px',fontSize:10,fontWeight:600,textTransform:'uppercase',letterSpacing:1,cursor:'pointer',
                background: introStep === 2 ? '#C29717' : 'transparent',
                color: introStep === 2 ? '#1a1a1a' : '#C29717',
                border: introStep === 2 ? 'none' : '1px solid rgba(194,151,23,0.3)',
                borderRadius:3,
              }}>{introStep === 1 ? 'Next' : 'I understood'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Click anywhere closes dropdowns */}
      {(pickerOpen || userOpen) && <div onClick={() => { setPickerOpen(false); setUserOpen(false) }} style={{ position:'fixed',top:0,left:0,right:0,bottom:0,zIndex:9999 }} />}

      {/* MAP */}
      <div style={{ visibility:viewMode==='map'?'visible':'hidden', height:viewMode==='map'?'auto':'0', overflow:viewMode==='map'?'visible':'hidden' }}>
        <Map coords={points} events={events} fetchEvents={fetchEvents} />
      </div>

      {/* INTEL */}
      {viewMode === 'intel' && (
        <div className='dailyreports-page' style={{ paddingTop:42,background:'#1a1a1a',minHeight:'100vh',textAlign:'left' }}>
          <DailyReports navigate={navigate} embedded={true} headerCountries={countries} headerDateFrom={dateFrom} headerDateTo={dateTo} />
        </div>
      )}
    </div>
  )
}

export default withRouter(App)
